import React from 'react';
import { MapPin, Clock, Video, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import AllPetCardSection from '../components/AllPetCardSection';

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const Users = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const InHomePrograms = () => {
  const dayTrainFeatures = [
    'This program is tailored for clients in the Tulsa area.',
    'Our trainer will visit your dog for daily sessions, teaching essential skills in a familiar setting.',
    'Your dog will learn obedience and socialization amidst your home, ranch, local feed stores, and parks, fostering a harmonious relationship right at your doorstep!',
    'With weekly lessons and daily update videos, you get to progress your skills right along side your dog!',
    'This program is very selective, save your spot now!'
  ];

  const lessonsFeatures = [
    'Enhance your dog\'s abilities with our private training lessons!',
    'With in-home lessons, you learn how to train and work your dog.',
    'This program requires dedication, commitment, and the desire to change your dog\'s life!',
    'Our 4-12 week packages are customized to work on your dog\'s specific needs and struggles.',
    'We address common issues in farm dogs, offering customized solutions for them.'
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>In-Home Dog Training Programs — Day Train & Private Lessons | Tulsa, Broken Arrow OK</title>
        <meta name="description" content="In-home dog training in Tulsa and surrounding areas. Day train programs and private lessons customized for your dog. Farm dog, ranch dog, and house dog training serving Broken Arrow, Owasso, Jenks, Bixby, Sand Springs, and more." />
        <link rel="canonical" href="https://farmdogoklahoma.com/in-home-programs" />
        <meta property="og:title" content="In-Home Dog Training — Farm Dog Oklahoma" />
        <meta property="og:description" content="Day train and private lesson programs in Tulsa and surrounding Oklahoma cities." />
        <meta property="og:url" content="https://farmdogoklahoma.com/in-home-programs" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#5C4033] to-[#3E2723] py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C4A052] rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              In Home Programs
            </h1>
            <p className="text-xl md:text-2xl text-[#C4A052] font-semibold">
              Train Your Dog Where It Matters Most
            </p>
          </div>
        </div>
      </section>

      {/* Day Train Section */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInUp} className="order-2 lg:order-1 space-y-6">
              <div className="inline-flex items-center gap-2 bg-[#C4A052] text-white px-4 py-2 rounded-full font-semibold">
                <Calendar size={20} />
                <span>Day Train Program</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Day Train
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our Day Train Program brings expert guidance to your home, land, and local haunts to build real-life skills and a well-mannered farm companion.
              </p>
              <div className="space-y-4">
                {dayTrainFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#C4A052] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{feature}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-4 pt-4">
                <a 
                  href="/contact"
                >
                  <Button className="bg-gradient-to-r from-[#C4A052] to-[#B8935E] hover:from-[#B8935E] hover:to-[#C4A052] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300">
                    Reserve Your Spot
                  </Button>
                </a>
              </div>
            </motion.div>
            <motion.div variants={fadeInUp} className="order-1 lg:order-2">
              <Card className="border-none shadow-2xl overflow-hidden hover-lift">
                <img 
                  src="/assets/images/1RhZJOH3RVSM2G4njmrZTe1YH_FZJregE.jpg" 
                  alt="Day train program" 
                  className="w-full h-96 object-cover object-center"
                />
                <CardContent className="p-6 bg-[#F5F5F0]">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="text-[#C4A052]" size={20} />
                      <span className="text-sm text-gray-700">Tulsa Area</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="text-[#C4A052]" size={20} />
                      <span className="text-sm text-gray-700">Daily Sessions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Video className="text-[#C4A052]" size={20} />
                      <span className="text-sm text-gray-700">Video Updates</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="text-[#C4A052]" size={20} />
                      <span className="text-sm text-gray-700">Weekly Lessons</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Lessons Section */}
      <motion.section initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={fadeInUp} className="section-padding bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeInUp}>
              <Card className="border-none shadow-2xl overflow-hidden hover-lift">
                <img 
                  src="/assets/images/1u70XdR42Xyl2WnJ0DdddVmLuvrG052g7.jpg" 
                  alt="Private lessons" 
                  className="w-full h-96 object-cover object-center"
                />
              </Card>
            </motion.div>
            <motion.div variants={fadeInUp} className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-[#C4A052] text-white px-4 py-2 rounded-full font-semibold">
                <Users size={20} />
                <span>Private Lessons</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#5C4033]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Lessons
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Learn how to train and work your dog with our customized private lessons. Perfect for owners who want to be hands-on in their dog's training journey.
              </p>
              <div className="space-y-4">
                {lessonsFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-[#C4A052] rounded-full mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">{feature}</p>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md">
                <h4 className="font-bold text-[#5C4033] mb-3">Program Duration</h4>
                <p className="text-gray-700 mb-2">Flexible 4-12 week packages customized to your dog's needs</p>
                <p className="text-sm text-[#C4A052] font-semibold">Commitment and dedication required for best results</p>
              </div>
              <div className="flex gap-4 pt-4">
                <a 
                  href="/contact"
                >
                  <Button className="bg-gradient-to-r from-[#C4A052] to-[#B8935E] hover:from-[#B8935E] hover:to-[#C4A052] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300">
                    Schedule Consultation
                  </Button>
                </a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* All Pet Card Section */}
      <AllPetCardSection />

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#C4A052] to-[#B8935E] py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Train Where It Matters Most
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Get personalized training in your home, on your land, and in your community!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="/contact"
            >
              <Button className="bg-white text-[#5C4033] hover:bg-gray-100 font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                Book Free Consult <ArrowRight className="ml-2" />
              </Button>
            </a>
            <a href="tel:+19189927332">
              <Button className="bg-[#5C4033] text-white hover:bg-[#3E2723] font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-xl">
                Call (918) 992-7332
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InHomePrograms;
